import { ReactNode, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useAppLoginContext } from "../../contexts/auth.contexts";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoginContract } from "../../../pages/auth/contracts/login.contracts";
import { loginSchema } from "../../../pages/auth/schema/login.schema";
import bg from "../../static/bg-login.png";
import bGmb from "../../static/bg-login-mb.png";
import { Button } from "../ui";

export function Login({ children }: { children: ReactNode }) {
  const { estaAutenticado, login } = useAppLoginContext();
  const [showLoader, setShowLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginContract>({
    resolver: yupResolver(loginSchema),
  });

  if (estaAutenticado) {
    return <>{children}</>;
  }

  const onSubmit = async (formData: LoginContract) => {
    setShowLoader(true);
    try {
      await login(formData);
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="flex h-screen w-full flex-col items-stretch justify-center overflow-hidden bg-slate-50 md:flex-row dark:bg-slate-900">
      <div className="flex h-[180px] min-h-[180px] w-full items-center justify-center md:h-screen md:min-h-screen md:w-3/5">
        <picture className="h-full w-full">
          <source media="(min-width: 900px)" srcSet={bg} />
          <img
            src={bGmb}
            alt="GWR"
            loading="lazy"
            className="h-full max-h-screen w-full object-cover"
          />
        </picture>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mx-auto flex w-full max-w-[90vw] flex-col justify-center gap-4 px-4 py-8 sm:max-w-[420px] md:w-2/5 md:px-6 md:py-0"
      >
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="email"
            className="text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:bg-slate-800 dark:text-white ${
              errors.email
                ? "border-red-400"
                : "border-slate-300 dark:border-slate-600"
            }`}
            {...register("email")}
          />
          {errors.email && (
            <span className="text-xs text-red-600">{errors.email.message}</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="password"
            className="text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Senha
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              className={`w-full rounded-lg border px-3 py-2.5 pr-10 text-sm text-slate-900 shadow-sm outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:bg-slate-800 dark:text-white ${
                errors.password
                  ? "border-red-400"
                  : "border-slate-300 dark:border-slate-600"
              }`}
              {...register("password")}
            />
            <button
              type="button"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              tabIndex={-1}
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <span className="text-xs text-red-600">
              {errors.password.message}
            </span>
          )}
        </div>

        <Button
          type="submit"
          loading={showLoader}
          disabled={showLoader}
          size="lg"
          className="mt-2 w-full"
        >
          Entrar
        </Button>
      </form>
    </div>
  );
}